import { useEffect, useState } from "react";
import axios from "axios";
import {
  Check,
  Copy,
  Download,
  Key,
} from "lucide-react";
import { useConfig } from "../../context/configContext";
import "./ChargerProvisioning.css";

export default function ChargerProvisioning() {
  const { config: appConfig } = useConfig();

  // Form Fields
  const [deviceId, setDeviceId] = useState("CHARGER_001");
  const [cpoId, setCpoId] = useState(appConfig.customer_id || "CPO_01");
  const [stationId, setStationId] = useState(appConfig.station_id || "UFC004");
  const [adminJwt, setAdminJwt] = useState("");

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // Copy Feedback State
  const [copied, setCopied] = useState({
    secret: false,
    clientCert: false,
    clientKey: false,
    caCert: false,
  });

  // Pull Admin Token on Mount
  useEffect(() => {
    const token = localStorage.getItem("access_token") || "";
    setAdminJwt(token);
  }, []);

  // API handler to provision credentials
  const handleProvision = async (e) => {
    e.preventDefault();
    if (!deviceId.trim() || !cpoId.trim() || !stationId.trim()) {
      setError("Device ID, CPO ID, and Station ID are required.");
      return;
    }
    if (!adminJwt.trim()) {
      setError("Admin Dashboard JWT is required to authorize provisioning.");
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const res = await axios({
        method: "POST",
        url: "/api/device-credentials",
        data: {
          device_id: deviceId.trim(),
          cpo_id: cpoId.trim(),
          station_id: stationId.trim(),
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminJwt}`,
        },
        validateStatus: () => true, // capture errors directly
      });

      if (res.status === 201 || res.status === 200) {
        setResults(res.data);
      } else {
        setError(res.data?.error || `Failed to provision (Status ${res.status})`);
      }
    } catch (err) {
      setError(`Network connection failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Clipboard copy handler
  const handleCopy = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied((prev) => ({ ...prev, [type]: true }));
    setTimeout(() => {
      setCopied((prev) => ({ ...prev, [type]: false }));
    }, 2000);
  };

  // File download helper
  const handleDownload = (filename, content) => {
    if (!content) return;
    const element = document.createElement("a");
    const file = new Blob([content], { type: "text/plain;charset=utf-8" });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Format expiration ISO timestamp to user-friendly local date string
  const formatExpiry = (isoString) => {
    if (!isoString) return "—";
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <div className="prov-container">
      {/* Form Card */}
      <section className="prov-panel">
        <div className="prov-panel-header">
          <div className="prov-panel-title">
            <span className="sim-step-badge config" style={{ background: "#0ea5e9" }}>
              <Key size={14} />
            </span>
            <h3>
              Generate Client Certificates & Secrets
              <span className="route">POST /api/device-credentials</span>
            </h3>
          </div>
        </div>

        <form onSubmit={handleProvision} className="prov-panel-body">
          <p className="prov-hint">
            Generate client certificates and symmetric key secrets required for Mutual TLS
            device authentication.
          </p>

          <div className="prov-grid-3">
            <div className="prov-field">
              <label htmlFor="deviceId">Device ID (CN)</label>
              <input
                type="text"
                id="deviceId"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="e.g. CHARGER_001"
                disabled={loading}
              />
            </div>
            <div className="prov-field">
              <label htmlFor="cpoId">CPO ID (Organization)</label>
              <input
                type="text"
                id="cpoId"
                value={cpoId}
                onChange={(e) => setCpoId(e.target.value)}
                placeholder="e.g. CPO_01"
                disabled={loading}
              />
            </div>
            <div className="prov-field">
              <label htmlFor="stationId">Station ID</label>
              <input
                type="text"
                id="stationId"
                value={stationId}
                onChange={(e) => setStationId(e.target.value)}
                placeholder="e.g. UFC004"
                disabled={loading}
              />
            </div>
            <div className="prov-field wide">
              <label htmlFor="adminJwt">Admin Dashboard JWT Token</label>
              <input
                type="password"
                id="adminJwt"
                value={adminJwt}
                onChange={(e) => setAdminJwt(e.target.value)}
                placeholder="Paste dashboard access token if not logged in"
                disabled={loading}
              />
            </div>
          </div>

          <div className="prov-action-row">
            <button
              type="submit"
              className="prov-btn prov-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="prov-spinner"></span> Generating Credentials...
                </>
              ) : (
                <>🔑 Generate Credentials</>
              )}
            </button>
          </div>

          {error && (
            <div className="prov-alert error">
              <strong>Error:</strong> {error}
            </div>
          )}
        </form>
      </section>

      {/* Results Section */}
      {results && (
        <section className="prov-panel">
          <div className="prov-panel-header" style={{ background: "#f0fdf4" }}>
            <div className="prov-panel-title">
              <h3 style={{ color: "#166534" }}>✓ Credentials Generated Successfully</h3>
            </div>
          </div>

          <div className="prov-panel-body">
            {/* Metadata Summary */}
            <div className="prov-metadata-grid">
              <div className="prov-meta-item">
                <span>Device ID</span>
                <strong>{results.device_id}</strong>
              </div>
              <div className="prov-meta-item">
                <span>CPO ID</span>
                <strong>{results.cpo_id}</strong>
              </div>
              <div className="prov-meta-item">
                <span>Station ID</span>
                <strong>{results.station_id || "—"}</strong>
              </div>
              <div className="prov-meta-item">
                <span>Cert Serial</span>
                <strong>{results.cert_serial || "—"}</strong>
              </div>
              <div className="prov-meta-item">
                <span>Cert Expires</span>
                <strong>{formatExpiry(results.cert_expires)}</strong>
              </div>
            </div>

            {/* Secret Code Block */}
            <div className="prov-secret-block">
              <div className="prov-secret-copy">
                <span>Device JWT Secret (Symmetric Key)</span>
                <code>{results.device_jwt_secret}</code>
              </div>
              <button
                type="button"
                className="prov-btn prov-btn-secondary"
                style={{ padding: "8px 12px" }}
                onClick={() => handleCopy(results.device_jwt_secret, "secret")}
              >
                {copied.secret ? <Check size={16} color="#059669" /> : <Copy size={16} />}
                <span>{copied.secret ? "Copied" : "Copy"}</span>
              </button>
            </div>

            {/* PEM certificates display */}
            <div className="prov-pem-grid">
              {/* 1. Client Certificate PEM */}
              <div className="prov-pem-card">
                <div className="prov-pem-header">
                  <span>Client Certificate ({results.device_id}_client.crt)</span>
                  <div className="prov-pem-actions">
                    <button
                      type="button"
                      className="prov-pem-btn-action"
                      title="Copy Certificate PEM"
                      onClick={() => handleCopy(results.client_cert_pem, "clientCert")}
                    >
                      {copied.clientCert ? (
                        <Check size={16} color="#34d399" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="prov-pem-btn-action"
                      title="Download Certificate File"
                      onClick={() =>
                        handleDownload(
                          `${results.device_id}_client.crt`,
                          results.client_cert_pem,
                        )
                      }
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <pre className="prov-pem-body">{results.client_cert_pem}</pre>
              </div>

              {/* 2. Client Private Key PEM */}
              <div className="prov-pem-card">
                <div className="prov-pem-header">
                  <span>Client Private Key ({results.device_id}_client.key)</span>
                  <div className="prov-pem-actions">
                    <button
                      type="button"
                      className="prov-pem-btn-action"
                      title="Copy Key PEM"
                      onClick={() => handleCopy(results.client_key_pem, "clientKey")}
                    >
                      {copied.clientKey ? (
                        <Check size={16} color="#34d399" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="prov-pem-btn-action"
                      title="Download Key File"
                      onClick={() =>
                        handleDownload(
                          `${results.device_id}_client.key`,
                          results.client_key_pem,
                        )
                      }
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <pre className="prov-pem-body">{results.client_key_pem}</pre>
              </div>

              {/* 3. CA Certificate PEM */}
              <div className="prov-pem-card">
                <div className="prov-pem-header">
                  <span>CA Certificate (ca.crt)</span>
                  <div className="prov-pem-actions">
                    <button
                      type="button"
                      className="prov-pem-btn-action"
                      title="Copy CA PEM"
                      onClick={() => handleCopy(results.ca_cert_pem, "caCert")}
                    >
                      {copied.caCert ? (
                        <Check size={16} color="#34d399" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="prov-pem-btn-action"
                      title="Download CA File"
                      onClick={() => handleDownload("ca.crt", results.ca_cert_pem)}
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <pre className="prov-pem-body">{results.ca_cert_pem}</pre>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
